/**
 * LeftShield Portal - Runtime Enforcement Page
 * Real-time operational dashboard for system health, active runs,
 * enforcement decisions, and alerts.
 *
 * Route: /control-room | Nav: Operations > Runtime Enforcement
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title1,
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  Button,
  Spinner,
  ProgressBar,
  tokens,
} from '@fluentui/react-components';
import {
  HeartPulseRegular,
  PlayRegular,
  ShieldCheckmarkRegular,
  WarningRegular,
  ArrowSyncRegular,
  SearchRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, runsApi, diagnosticApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { GovernanceRuntimeMap } from '../components/governance/GovernanceRuntimeMap';
import { RiskPropagationMap } from '../components/risk/RiskPropagationMap';
import { EventStream } from '../components/activity/EventStream';
import { PageHeader } from '../components/common/PageHeader';
import { EmptyStateCard } from '../components/common/EmptyStateCard';
import { ConfidenceTrendSparkline } from '../components/confidence';
import type {
  DashboardOverview,
  EnforcementOutcomes,
  Run,
  RuntimeBlindSpotsSummary,
} from '../types';

const getRunStatusColor = (status: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Failed':
      return 'danger';
    case 'Running':
      return 'informative';
    case 'Cancelled':
      return 'warning';
    default:
      return 'informative';
  }
};

const getBlindSpotSeverityColor = (severity: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (severity?.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

export const ControlRoomPage: React.FC = () => {
  const ps = usePageStyles();
  const navigate = useNavigate();

  const { data: overview, isLoading: overviewLoading } = useQuery<DashboardOverview>({
    queryKey: ['controlRoom', 'overview'],
    queryFn: () => dashboardApi.getDashboardOverview(),
  });

  const { data: enforcement, isLoading: enforcementLoading } = useQuery<EnforcementOutcomes>({
    queryKey: ['controlRoom', 'enforcement'],
    queryFn: () => dashboardApi.getEnforcementOutcomes(),
  });

  const { data: recentRuns, isLoading: runsLoading } = useQuery<Run[]>({
    queryKey: ['controlRoom', 'recentRuns'],
    queryFn: () => runsApi.getRecent(5),
    refetchInterval: 10000,
  });

  const { data: blindSpots, isLoading: blindSpotsLoading } = useQuery<RuntimeBlindSpotsSummary>({
    queryKey: ['controlRoom', 'blindSpots'],
    queryFn: () => diagnosticApi.getRuntimeBlindSpotsSummary(),
  });

  const isLoading = overviewLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading control room..." />
      </div>
    );
  }

  const postureScore = overview?.postureScore ?? 0;
  const enforcementRate = overview?.enforcementRate ?? 0;
  const criticalFindings = overview?.criticalFindings ?? 0;
  const governanceScore = overview?.governanceScore ?? 0;
  const allZero = postureScore === 0 && enforcementRate === 0 && criticalFindings === 0 && governanceScore === 0;

  return (
    <div className={ps.page}>
      <PageHeader
        title="Runtime Enforcement"
        subtitle="Live policy evaluation decisions — blocks, warnings, and approvals"
      />

      {/* Enforcement Operations Card */}
      <Card>
        <CardHeader
          image={
            <HeartPulseRegular
              style={{
                fontSize: '2rem',
                color: allZero
                  ? tokens.colorNeutralForeground3
                  : enforcementRate >= 80
                  ? tokens.colorPaletteGreenForeground1
                  : enforcementRate >= 50
                  ? tokens.colorPaletteYellowForeground1
                  : tokens.colorPaletteRedForeground1,
              }}
            />
          }
          header={<Title2>Runtime Enforcement Health</Title2>}
          description={
            allZero ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalL }}>
                <Body1 style={{ color: tokens.colorNeutralForeground3 }}>Not yet activated</Body1>
                <Badge appearance="tint" color="informative">Pending</Badge>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalL }}>
                <Title1
                  style={{
                    color: enforcementRate >= 80
                      ? tokens.colorPaletteGreenForeground1
                      : enforcementRate >= 50
                      ? tokens.colorPaletteYellowForeground1
                      : tokens.colorPaletteRedForeground1,
                  }}
                >
                  {enforcementRate}%
                </Title1>
                <ProgressBar
                  value={enforcementRate / 100}
                  thickness="large"
                  style={{ width: '200px' }}
                />
                <Badge
                  appearance="filled"
                  color={enforcementRate >= 80 ? 'success' : enforcementRate >= 50 ? 'warning' : 'danger'}
                >
                  {enforcementRate >= 80 ? 'Healthy' : enforcementRate >= 50 ? 'Degraded' : 'Critical'}
                </Badge>
              </div>
            )
          }
        />
        <div className={ps.cardBody}>
          <div className={ps.statGrid}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Enforcement Rate</Body2>
              <Title2 className="metric-value" style={{ color: allZero ? tokens.colorNeutralForeground4 : undefined }}>{enforcementRate}%</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Policies Executed Today</Body2>
              <Title2 className="metric-value" style={{ color: allZero ? tokens.colorNeutralForeground4 : undefined }}>
                {overview?.enforcement?.totalEvaluations ?? 0}
              </Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Critical Findings</Body2>
              <Title2 className="metric-value" style={{ color: criticalFindings > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground4 }}>
                {criticalFindings}
              </Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Governance Score</Body2>
              <Title2 className="metric-value" style={{ color: allZero ? tokens.colorNeutralForeground4 : undefined }}>{governanceScore}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Remediation Velocity</Body2>
              <Title2 className="metric-value">{overview?.remediationVelocity && overview.remediationVelocity !== 'N/A' ? overview.remediationVelocity : 'No data yet'}</Title2>
            </div>
          </div>
          {!allZero && (
            <div style={{ marginTop: tokens.spacingVerticalM }}>
              <Body2 style={{ color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalXS }}>
                Confidence Trend (7d)
              </Body2>
              <ConfidenceTrendSparkline data={[72, 74, 73, 76, 78, 80, 82]} width={160} height={32} />
            </div>
          )}
        </div>
      </Card>

      {/* Governance Runtime Map */}
      <Card>
        <CardHeader header={<Title2>Governance Runtime Map</Title2>} />
        <div className={ps.cardBody}>
          <GovernanceRuntimeMap variant="compact" />
        </div>
      </Card>

      {/* Two column: Active Runs + Enforcement Decisions */}
      <div className={ps.twoColumn}>
        {/* Active Runs Card */}
        <Card>
          <CardHeader
            image={<PlayRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />}
            header={<Title2>Active Runs</Title2>}
            description={<Body2>Last 5 runs (auto-refreshing)</Body2>}
          />
          <div className={ps.cardBody}>
            {runsLoading ? (
              <Spinner size="small" label="Loading runs..." />
            ) : recentRuns && recentRuns.length > 0 ? (
              <div className={ps.rowList}>
                {recentRuns.map((run) => (
                  <div
                    key={run.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: tokens.spacingVerticalS,
                      padding: tokens.spacingVerticalS,
                      borderRadius: tokens.borderRadiusSmall,
                      backgroundColor: tokens.colorNeutralBackground2,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Body1 style={{ fontWeight: 500 }}>{run.name}</Body1>
                      <Badge appearance="filled" color={getRunStatusColor(run.status)}>
                        {run.status}
                      </Badge>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                        {run.targetIdentifier}
                      </Body2>
                      {run.totalFindings > 0 && (
                        <Badge appearance="tint" color="danger" size="small">
                          {run.totalFindings} findings
                        </Badge>
                      )}
                    </div>
                    {run.startedAt && (
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                        {new Date(run.startedAt).toLocaleString()}
                      </Body2>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyStateCard
                icon={<PlayRegular />}
                title="No Policy Runs Yet"
                description="Policy runs appear here as your delivery pipelines are governed. Connect a CI pipeline or trigger a manual scan to get started."
                actionLabel="View Pipelines"
                onAction={() => window.location.href = '/runs'}
              />
            )}
          </div>
        </Card>

        {/* Enforcement Decision Stream Card */}
        <Card>
          <CardHeader
            image={<ShieldCheckmarkRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />}
            header={<Title2>Enforcement Decisions</Title2>}
            description={<Body2>Allow / Warn / Block outcomes</Body2>}
          />
          <div className={ps.cardBody}>
            {enforcementLoading ? (
              <Spinner size="small" label="Loading enforcement data..." />
            ) : enforcement ? (
              <>
                <div className={ps.statGrid}>
                  <div className={ps.statTile}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Allowed</Body2>
                    <Title2 style={{ color: tokens.colorPaletteGreenForeground1 }}>
                      {enforcement.allowedCount}
                    </Title2>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      {enforcement.allowRate.toFixed(1)}%
                    </Body2>
                  </div>
                  <div className={ps.statTile}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Warned</Body2>
                    <Title2 style={{ color: tokens.colorPaletteYellowForeground1 }}>
                      {enforcement.warnedCount}
                    </Title2>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      {enforcement.warnRate.toFixed(1)}%
                    </Body2>
                  </div>
                  <div className={ps.statTile}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Blocked</Body2>
                    <Title2 style={{ color: tokens.colorPaletteRedForeground1 }}>
                      {enforcement.blockedCount}
                    </Title2>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      {enforcement.blockRate.toFixed(1)}%
                    </Body2>
                  </div>
                </div>
                <div className={ps.inlineRow}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                    Total Evaluations: {enforcement.totalEvaluations}
                  </Body2>
                  {enforcement.blockRateTrend !== 0 && (
                    <Badge
                      appearance="tint"
                      color={enforcement.blockRateTrend > 0 ? 'danger' : 'success'}
                    >
                      Block rate {enforcement.blockRateTrend > 0 ? '+' : ''}{enforcement.blockRateTrend.toFixed(1)}%
                    </Badge>
                  )}
                </div>
              </>
            ) : (
              <EmptyStateCard
                icon={<ShieldCheckmarkRegular />}
                title="Enforcement Engine Idle"
                description="No enforcement events yet. Configure policies to start governing your pipelines. Decisions will stream here in real time."
                actionLabel="Configure Policies"
                onAction={() => window.location.href = '/guardrails'}
              />
            )}
          </div>
        </Card>
      </div>

      {/* Alerts Card */}
      <Card>
        <CardHeader
          image={<WarningRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground1 }} />}
          header={<Title2>Alerts</Title2>}
          description={<Body2>Top blind spots by severity</Body2>}
        />
        <div className={ps.cardBody}>
          {blindSpotsLoading ? (
            <Spinner size="small" label="Loading alerts..." />
          ) : blindSpots && blindSpots.topBlindSpots.length > 0 ? (
            <div className={ps.rowList}>
              {blindSpots.topBlindSpots
                .filter((bs) => bs.severity === 'Critical' || bs.severity === 'High')
                .slice(0, 5)
                .map((bs) => (
                  <div key={bs.id} className={ps.rowItem}>
                    <div>
                      <Body1 style={{ fontWeight: 500 }}>{bs.blindSpotType.replace(/_/g, ' ')}</Body1>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                        {bs.scopeType}: {bs.scopeId}
                      </Body2>
                    </div>
                    <div className={ps.inlineRow}>
                      <Badge appearance="filled" color={getBlindSpotSeverityColor(bs.severity)}>
                        {bs.severity}
                      </Badge>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                        {bs.recommendedAction}
                      </Body2>
                    </div>
                  </div>
                ))}
              {blindSpots.topBlindSpots.filter(
                (bs) => bs.severity === 'Critical' || bs.severity === 'High'
              ).length === 0 && (
                <Body1 className={ps.emptyState}>No critical or high severity alerts.</Body1>
              )}
            </div>
          ) : (
            <EmptyStateCard
              icon={<WarningRegular />}
              title="No Governance Blind Spots Detected"
              description="All governed areas are covered. Run a diagnostic scan to verify coverage across your delivery infrastructure."
              actionLabel="Run Diagnostic"
              onAction={() => window.location.href = '/diagnostic'}
            />
          )}
        </div>
      </Card>

      {/* Risk Propagation — F045: same capability as Risk -> Attack Paths, cross-linked rather
          than redirected (this page is Runtime Enforcement, much broader than attack paths). */}
      <Card>
        <CardHeader
          header={<Title2>Risk Propagation</Title2>}
          action={
            <Button appearance="subtle" size="small" onClick={() => navigate('/attack-paths')}>
              View Full Attack Paths
            </Button>
          }
        />
        <div className={ps.cardBody}>
          <RiskPropagationMap />
        </div>
      </Card>

      {/* Live Event Stream */}
      <Card>
        <CardHeader
          header={<Title2>Live Events</Title2>}
          description={<Body2>Recent system activity</Body2>}
        />
        <div className={ps.cardBody}>
          <EventStream />
        </div>
      </Card>

      {/* Quick Action Buttons */}
      <div className={ps.inlineRow}>
        <Button
          appearance="primary"
          icon={<ArrowSyncRegular />}
          as="a"
          href="/runs"
        >
          Trigger Scan
        </Button>
        <Button
          appearance="secondary"
          icon={<SearchRegular />}
          as="a"
          href="/diagnostic"
        >
          View Diagnostics
        </Button>
        <Button
          appearance="secondary"
          icon={<ArrowDownloadRegular />}
          as="a"
          href="/evidence"
        >
          Export Evidence
        </Button>
      </div>
    </div>
  );
};
